import React, { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  chdCodeService, 
  CHDCode, 
  CHDCodeData, 
  CHDCodeFilters 
} from '@/services/chdCodeService';
import { useToast } from '@/hooks/use-toast';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  AlertTriangle,
  Check,
  Settings,
  Heart,
  X,
  Shield,
  ShieldAlert
} from 'lucide-react';

// Table columns configuration
const columns = [
  {
    name: 'Cod ICD-10',
    selector: (row: CHDCode) => row.icd10_code,
    sortable: true,
    compact: true,
    minWidth: '100px',
    maxWidth: '120px',
    cell: (row: CHDCode) => (
      <div className="font-mono font-medium text-sm">
        {row.icd10_code}
      </div>
    ),
  },
  {
    name: 'Descriere română',
    selector: (row: CHDCode) => row.description_ro,
    sortable: true,
    grow: 2,
    wrap: true,
    cell: (row: CHDCode) => (
      <div className="text-sm py-1">
        {row.description_ro}
      </div>
    ),
  },
  {
    name: 'Descriere engleză',
    selector: (row: CHDCode) => row.description_en,
    sortable: true,
    grow: 1.5,
    wrap: true,
    hide: 768,
    cell: (row: CHDCode) => (
      <div className="text-sm py-1">
        {row.description_en}
      </div>
    ),
  },
  {
    name: 'EUROCAT',
    selector: (row: CHDCode) => row.eurocat_subgroup || '',
    sortable: true,
    compact: true,
    minWidth: '100px',
    maxWidth: '140px',
    cell: (row: CHDCode) => (
      row.eurocat_subgroup ? (
        <Badge variant="outline" className="text-xs">
          {row.eurocat_subgroup}
        </Badge>
      ) : (
        <span className="text-muted-foreground text-xs">-</span>
      )
    ),
  },
  {
    name: 'Sever',
    selector: (row: CHDCode) => row.is_severe_chd,
    sortable: true,
    compact: true,
    center: true,
    width: '80px',
    cell: (row: CHDCode) => (
      row.is_severe_chd ? (
        <Badge variant="destructive" className="text-xs">
          <ShieldAlert className="h-3 w-3" />
        </Badge>
      ) : (
        <Badge variant="secondary" className="text-xs">
          <Shield className="h-3 w-3" />
        </Badge>
      )
    ),
  },
  {
    name: 'Uz',
    selector: (row: CHDCode) => row.usage_count || 0,
    sortable: true,
    compact: true,
    center: true,
    width: '60px',
    cell: (row: CHDCode) => (
      <Badge variant="secondary" className="text-xs">
        {row.usage_count || 0}
      </Badge>
    ),
  },
];

export const CHDCodesManagement: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [chdCodes, setChdCodes] = useState<CHDCode[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CHDCode | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<CHDCodeData>({
    icd10_code: '',
    description_ro: '',
    description_en: '',
    eurocat_subgroup: '',
    is_severe_chd: false
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters: CHDCodeFilters = {
        page: currentPage,
        limit: perPage,
        search: searchTerm.trim() || undefined,
        sortBy: 'icd10_code',
        sortOrder: 'asc'
      };

      const response = await chdCodeService.listCHDCodes(filters);
      setChdCodes(response.data);
      setTotalRows(response.total);
      setLoading(false);
    } catch (error: any) {
      console.error('Error loading MCC codes:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la încărcarea codurilor MCC',
        variant: "destructive",
      });
      setLoading(false);
    }
  }, [currentPage, perPage, searchTerm, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.icd10_code.trim()) {
      errors.icd10_code = 'Codul ICD-10 este obligatoriu';
    } else {
      // Validate ICD-10 format
      const icd10Pattern = /^[A-Z]\d{2}(\.\d)?$/;
      if (!icd10Pattern.test(formData.icd10_code.trim().toUpperCase())) {
        errors.icd10_code = 'Formatul codului ICD-10 nu este valid (ex: Q21.0)';
      }
    }

    if (!formData.description_ro.trim()) {
      errors.description_ro = 'Descrierea în română este obligatorie';
    } else if (formData.description_ro.trim().length < 3) {
      errors.description_ro = 'Descrierea trebuie să aibă cel puțin 3 caractere';
    }

    if (!formData.description_en.trim()) {
      errors.description_en = 'Descrierea în engleză este obligatorie';
    } else if (formData.description_en.trim().length < 3) {
      errors.description_en = 'Descrierea trebuie să aibă cel puțin 3 caractere';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCode = async () => {
    if (!validateForm()) return;

    try {
      setActionLoading(true);
      await chdCodeService.createCHDCode(formData);
      
      toast({
        title: "Succes",
        description: 'Codul MCC a fost adăugat cu succes',
      });
      
      setShowAddDialog(false);
      resetForm();
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error creating MCC code:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la adăugarea codului MCC',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const handleEditCode = async () => {
    if (!selectedCode || !validateForm()) return;

    try {
      setActionLoading(true);
      await chdCodeService.updateCHDCode(selectedCode.id, formData);
      
      toast({
        title: "Succes",
        description: 'Codul MCC a fost actualizat cu succes',
      });
      
      setShowEditDialog(false);
      resetForm();
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error updating MCC code:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la actualizarea codului MCC',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const handleDeleteCode = async () => {
    if (!selectedCode) return;

    try {
      setActionLoading(true);
      await chdCodeService.deleteCHDCode(selectedCode.id);
      
      toast({
        title: "Succes",
        description: 'Codul MCC a fost șters cu succes',
      });
      
      setShowDeleteDialog(false);
      setSelectedCode(null);
      await loadData();
      setActionLoading(false);
    } catch (error: any) {
      console.error('Error deleting MCC code:', error);
      toast({
        title: "Eroare",
        description: error.message || 'Eroare la ștergerea codului MCC',
        variant: "destructive",
      });
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      icd10_code: '',
      description_ro: '',
      description_en: '',
      eurocat_subgroup: '',
      is_severe_chd: false
    });
    setFormErrors({});
    setSelectedCode(null);
  };

  const openEditDialog = (code: CHDCode) => {
    setSelectedCode(code);
    setFormData({
      icd10_code: code.icd10_code,
      description_ro: code.description_ro,
      description_en: code.description_en,
      eurocat_subgroup: code.eurocat_subgroup || '',
      is_severe_chd: code.is_severe_chd
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const openDeleteDialog = (code: CHDCode) => {
    setSelectedCode(code);
    setShowDeleteDialog(true);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  // Actions column for the table
  const actionsColumn = {
    name: 'Acțiuni',
    cell: (row: CHDCode) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEditDialog(row)}
          title="Editează codul MCC"
          className="p-1"
        >
          <Edit className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openDeleteDialog(row)}
          title="Șterge codul MCC"
          className="text-red-600 hover:text-red-700 p-1"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    compact: true,
    width: '90px',
  };

  const tableColumns = [...columns, actionsColumn];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading && currentPage === 1) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Coduri MCC</h1>
            <p className="text-muted-foreground">
              Gestionează codurile ICD-10 pentru malformațiile cardiace congenitale
            </p>
          </div>
          <Button onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Adaugă cod nou
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtrare și căutare</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Caută coduri MCC..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Caută
                </Button>
                {searchTerm && (
                  <Button
                    onClick={handleClearSearch}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Șterge
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Coduri MCC ({totalRows})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-hidden">
              <DataTable
                columns={tableColumns}
                data={chdCodes}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationDefaultPage={currentPage}
                paginationPerPage={perPage}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                progressPending={loading}
                noDataComponent="Nu există coduri MCC înregistrate"
                paginationComponentOptions={{
                  rowsPerPageText: 'Rânduri pe pagină:',
                  rangeSeparatorText: 'din',
                  selectAllRowsItem: true,
                  selectAllRowsItemText: 'Toate',
                }}
                dense
                responsive
                fixedHeader
                fixedHeaderScrollHeight="500px"
                customStyles={{
                  table: {
                    style: {
                      overflow: 'hidden',
                    },
                  },
                  tableWrapper: {
                    style: {
                      overflow: 'hidden',
                    },
                  },
                  responsiveWrapper: {
                    style: {
                      overflow: 'hidden',
                    },
                  },
                  cells: {
                    style: {
                      paddingLeft: '8px',
                      paddingRight: '8px',
                    },
                  },
                  headCells: {
                    style: {
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      fontSize: '0.875rem',
                    },
                  },
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Adaugă cod MCC nou
              </DialogTitle>
              <DialogDescription>
                Completează informațiile pentru noul cod MCC
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="icd10_code">Cod ICD-10*</Label>
                <Input
                  id="icd10_code"
                  value={formData.icd10_code}
                  onChange={(e) => setFormData({...formData, icd10_code: e.target.value.toUpperCase()})}
                  placeholder="ex. Q21.0"
                />
                {formErrors.icd10_code && (
                  <p className="text-sm text-red-600">{formErrors.icd10_code}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description_ro">Descriere română*</Label>
                <Input
                  id="description_ro"
                  value={formData.description_ro}
                  onChange={(e) => setFormData({...formData, description_ro: e.target.value})}
                  placeholder="ex. Defect septal ventricular"
                />
                {formErrors.description_ro && (
                  <p className="text-sm text-red-600">{formErrors.description_ro}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description_en">Descriere engleză*</Label>
                <Input
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  placeholder="ex. Ventricular septal defect"
                />
                {formErrors.description_en && (
                  <p className="text-sm text-red-600">{formErrors.description_en}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="eurocat_subgroup">Subgrup EUROCAT</Label>
                <Input
                  id="eurocat_subgroup"
                  value={formData.eurocat_subgroup}
                  onChange={(e) => setFormData({...formData, eurocat_subgroup: e.target.value})}
                  placeholder="ex. Septal defects"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_severe_chd"
                  checked={formData.is_severe_chd}
                  onCheckedChange={(checked) => 
                    setFormData({...formData, is_severe_chd: Boolean(checked)})
                  }
                />
                <Label htmlFor="is_severe_chd">MCC sever</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button onClick={handleAddCode} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Adaugă...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Editează cod MCC
              </DialogTitle>
              <DialogDescription>
                Modifică informațiile codului MCC
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_icd10_code">Cod ICD-10*</Label>
                <Input
                  id="edit_icd10_code"
                  value={formData.icd10_code}
                  onChange={(e) => setFormData({...formData, icd10_code: e.target.value.toUpperCase()})}
                />
                {formErrors.icd10_code && (
                  <p className="text-sm text-red-600">{formErrors.icd10_code}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_description_ro">Descriere română*</Label>
                <Input
                  id="edit_description_ro"
                  value={formData.description_ro}
                  onChange={(e) => setFormData({...formData, description_ro: e.target.value})}
                />
                {formErrors.description_ro && (
                  <p className="text-sm text-red-600">{formErrors.description_ro}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_description_en">Descriere engleză*</Label>
                <Input
                  id="edit_description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                />
                {formErrors.description_en && (
                  <p className="text-sm text-red-600">{formErrors.description_en}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit_eurocat_subgroup">Subgrup EUROCAT</Label>
                <Input
                  id="edit_eurocat_subgroup"
                  value={formData.eurocat_subgroup}
                  onChange={(e) => setFormData({...formData, eurocat_subgroup: e.target.value})}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit_is_severe_chd"
                  checked={formData.is_severe_chd}
                  onCheckedChange={(checked) => 
                    setFormData({...formData, is_severe_chd: Boolean(checked)})
                  }
                />
                <Label htmlFor="edit_is_severe_chd">MCC sever</Label>
              </div>
              {selectedCode && selectedCode.usage_count && selectedCode.usage_count > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Acest cod MCC este folosit în {selectedCode.usage_count} diagnostice.
                    Modificările vor afecta toate diagnosticele existente.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button onClick={handleEditCode} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Salvează...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Confirmare ștergere
              </DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>Ești sigur că vrei să ștergi codul MCC <strong>{selectedCode?.icd10_code} - {selectedCode?.description_ro}</strong>?</p>
                  {selectedCode && selectedCode.usage_count && selectedCode.usage_count > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Atenție!</strong> Acest cod MCC este folosit în {selectedCode.usage_count} diagnostice.
                        Ștergerea va afecta toate aceste diagnostice și poate cauza probleme în sistem.
                      </AlertDescription>
                    </Alert>
                  )}
                  <p className="text-red-600 font-medium">
                    Această acțiune este ireversibilă.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={actionLoading}>
                Anulează
              </Button>
              <Button variant="destructive" onClick={handleDeleteCode} disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Settings className="mr-2 h-4 w-4 animate-spin" />
                    Șterge...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Șterge definitiv
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};